import { useCallback } from 'react';

import { Command } from '@dreryk/command-query-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { Bean, FormContextValues, PromiseVoidFunction, TranslatableText } from '@dreryk/react-common';
import {
   ActionBar,
   ButtonVariant,
   dispatchSnackbarPositiveEvent,
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
} from '@dreryk/react-components';

import { FormsTemplateListItemId } from './FormsTemplates.types';
import { useFormsTemplatesDisplayContext } from './displayContext/formsTemplatesDisplayContext';
import { useFormsTemplatesContext } from './formsTemplatesContext';
import { formsTemplatesTranslationKeys } from './translations';

type FormsTemplatesSendTemplateButtonProps<FORM_BEAN extends Bean, RESULT extends Bean> = {
   readonly handleSubmit: FormContextValues<FORM_BEAN>['handleSubmit'];
   readonly onSuccess: PromiseVoidFunction;
   readonly disabled: boolean;
   readonly commandCreator: (parameters: FORM_BEAN, templateId: FormsTemplateListItemId<RESULT>) => Command<RESULT>;
   readonly shouldConfirm?: {
      enabled: boolean;
      title: TranslatableText;
      content: TranslatableText;
   };
};

// eslint-disable-next-line max-lines-per-function
export const FormsTemplatesSendTemplateButton = <FORM_BEAN extends Bean, RESULT extends Bean>(
   props: FormsTemplatesSendTemplateButtonProps<FORM_BEAN, RESULT>
): JSX.Element | null => {
   const { setReadOnlyItem, enableSwitching, activeItem, mode } = useFormsTemplatesContext<RESULT>();
   const displayContext = useFormsTemplatesDisplayContext();

   const handleTemplateSaveSuccess = useCallback(
      async (savedTemplate: RESULT): Promise<void> => {
         setReadOnlyItem(savedTemplate);
         enableSwitching();
         await props.onSuccess();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [enableSwitching, props.onSuccess, setReadOnlyItem]
   );

   const { executeAsync: createTemplate } = useCqrsCommand<RESULT, FORM_BEAN>(
      (parameters) => props.commandCreator(parameters, activeItem!.id),
      {
         onSuccess: (result) => {
            dispatchSnackbarPositiveEvent(formsTemplatesTranslationKeys.templateSavedSuccessfully);
            handleTemplateSaveSuccess(result);
         },
      }
   );

   if (mode !== 'EDIT') {
      return null;
   }

   if (displayContext === 'MODAL') {
      return (
         <ModalConfirmButton
            label={formsTemplatesTranslationKeys.saveTemplate}
            onClick={props.handleSubmit(async (values) => createTemplate(values as FORM_BEAN))}
            disabled={props.disabled}
            shouldConfirm={
               props.shouldConfirm?.enabled
                  ? {
                       modalWidth: ModalWidth.NORMAL,
                       confirmationButtonLabel: formsTemplatesTranslationKeys.save,
                       ...props.shouldConfirm,
                    }
                  : undefined
            }
         />
      );
   }

   if (props.shouldConfirm?.enabled) {
      return (
         <ShowModalEventInterceptor
            testId={'templateConfirm'}
            title={props.shouldConfirm.title}
            content={props.shouldConfirm.content}
            confirmationButtonLabel={formsTemplatesTranslationKeys.save}
            modalWidth={ModalWidth.NORMAL}
            shouldShowModal
         >
            <ActionBar.PrimaryButton
               testId={'saveTemplate'}
               label={formsTemplatesTranslationKeys.save}
               onClick={props.handleSubmit(async (values) => createTemplate(values as FORM_BEAN))}
               disabled={props.disabled}
               order={1}
               variant={ButtonVariant.PRIMARY}
            />
         </ShowModalEventInterceptor>
      );
   }

   return (
      <ActionBar.PrimaryButton
         testId={'saveTemplate'}
         label={formsTemplatesTranslationKeys.save}
         onClick={props.handleSubmit(async (values) => createTemplate(values as FORM_BEAN))}
         disabled={props.disabled}
         order={1}
         variant={ButtonVariant.PRIMARY}
      />
   );
};
